import { useState } from 'react';

/**
 * Presentational image thumbnail with a graceful fallback (04 #3). When `src` is null/absent —
 * or the request 404s (e.g. a just-deleted blob mid-cache) — it renders the first letter of
 * `name` on a tinted disc instead of a broken-image icon. Images are served same-origin through
 * the SWA proxy (`/api/avatar?userId=` / `/api/client-logo?clientId=`), so the auth cookie rides
 * along automatically. Purely visual: callers decide WHEN to render it (e.g. the header omits it
 * entirely when no client logo exists).
 */
export function AvatarImage({
  src,
  name = '',
  size = 32,
  shape = 'circle',
  alt,
  className = '',
}: {
  src?: string | null;
  name?: string;
  size?: number;
  shape?: 'circle' | 'square';
  alt?: string;
  className?: string;
}): JSX.Element {
  const [failed, setFailed] = useState(false);
  const radius = shape === 'circle' ? 'rounded-full' : 'rounded-md';
  const dim = { width: size, height: size } as const;
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  if (!src || failed) {
    return (
      <span
        aria-label={alt ?? name}
        style={dim}
        className={[
          'inline-flex items-center justify-center shrink-0 select-none',
          'bg-[var(--color-accent)]/25 text-[var(--ink-brand)] font-medium',
          radius,
          className,
        ].join(' ')}
      >
        <span style={{ fontSize: Math.max(11, Math.round(size * 0.42)) }}>{initial}</span>
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt ?? name}
      style={dim}
      onError={() => setFailed(true)}
      className={['object-cover shrink-0 border border-[rgb(var(--edge-rgb)/0.10)]', radius, className].join(' ')}
    />
  );
}
